"use client"

import { useMemo, useState } from "react"
import { ArrowUpRight, Info, RefreshCw } from "lucide-react"
import {
  StatBorderGrid,
  ToolPageShell,
  ToolProductCarousel,
  ToolShareBar,
} from "@/components/brand-kit"
import type { ToolComponentData } from "@/lib/tool-adapter"
import { readCount, readNonNegative, readQuantity } from '@/lib/tools/numeric-input'

// Standard amortization formula:
//   P × r(1+r)^n / ((1+r)^n − 1)
// where P = principal, r = monthly rate (APR/12), n = months.
// 0% APR special case: monthly = principal / months.

const TERM_OPTIONS = [12, 24, 36, 48, 60] as const

const RELATED = [
  {
    title: "Refrigerator Payment Estimator",
    category: "Energy & Cost",
    description: "Quick monthly payment ranges across all terms.",
    href: "/tools/refrigerator-payment-estimator",
    time: "~30 sec",
  },
  {
    title: "Financing Comparison Tool",
    category: "Specialty",
    description: "Compare 0% APR offers vs financing.",
    href: "/tools/financing-comparison-tool",
    time: "~45 sec",
  },
  {
    title: "Refrigerator Cost of Ownership Calculator",
    category: "Energy & Cost",
    description: "True 10-year purchase + energy + maintenance cost.",
    href: "/tools/refrigerator-cost-of-ownership-calculator",
    time: "~45 sec",
  },
  {
    title: "Refrigerator Rebate Finder",
    category: "Energy & Cost",
    description: "Up to $500 back on ENERGY STAR upgrades.",
    href: "/tools/refrigerator-rebate-finder",
    time: "~30 sec",
  },
]

export function RefrigeratorFinancingCalculatorClient(_props: { toolData?: ToolComponentData } = {}) {
  const [price, setPrice] = useState("1500")
  const [term, setTerm] = useState("36")
  const [apr, setApr] = useState("9.99")

  const result = useMemo(() => {
    const p = readQuantity(price, { min: 1, max: 50000 })
    const months = readCount(term, { min: 1, max: 120 })
    const r = readNonNegative(apr, { max: 35 })
    if (!p || !months || r === null) return null

    let monthly: number
    let total: number
    let interest: number
    if (r === 0) {
      monthly = p / months
      total = p
      interest = 0
    } else {
      const monthlyRate = r / 100 / 12
      monthly =
        (p * (monthlyRate * Math.pow(1 + monthlyRate, months))) /
        (Math.pow(1 + monthlyRate, months) - 1)
      total = monthly * months
      interest = total - p
    }

    return {
      monthly,
      total,
      interest,
      months,
      principal: p,
      apr: r,
      interestPctOfPrice: p > 0 ? (interest / p) * 100 : 0,
    }
  }, [price, term, apr])

  return (
    <ToolPageShell
      toolName="Refrigerator Financing Calculator"
      heroEyebrow="Energy & Cost"
      heroTitlePre="What will you actually pay"
      heroTitleItalic="per month"
      heroTitlePost="for that fridge?"
      heroSubtitle="Standard amortization math: enter price, term, and APR — see your monthly payment, total cost, and how much of that is interest. 0% APR is fully supported."
      heroMeta={["⏱ ~30 sec", "Standard amortization formula"]}
      heroShare={<ToolShareBar title="Refrigerator Financing Calculator" />}
      heroRight={
        <StatBorderGrid
          stats={[
            { value: "P×r(1+r)ⁿ", label: "Standard amortization", note: "÷ ((1+r)ⁿ − 1)" },
            { value: "0%", label: "Promo APR supported", note: "Equal monthly split" },
            { value: "12–60", label: "Months supported", note: "Common terms" },
            { value: "Live", label: "Updates as you type", note: "No submit button" },
          ]}
          className="w-full max-w-md md:max-w-none md:w-auto mx-auto md:mx-0"
        />
      }
      faqs={[
        {
          question: "What formula does this use?",
          answer:
            "The standard amortization formula: monthly payment = P × r(1+r)ⁿ / ((1+r)ⁿ − 1), where P is the principal, r is the monthly rate (APR ÷ 12), and n is the number of months.",
        },
        {
          question: "How does it handle 0% APR offers?",
          answer:
            "When APR is 0, monthly payment = principal ÷ months — equal payments with no interest. Useful for comparing promotional financing to standard rates.",
        },
        {
          question: "What APR should I plug in?",
          answer:
            "Use the rate the retailer or lender is offering you specifically. Store-card APRs run high (often 20–29%); personal loan APRs typically run lower. Get the actual offer in writing first — advertised rates are often best-case.",
        },
        {
          question: "Does this account for sales tax or delivery fees?",
          answer:
            "No — enter the all-in financed amount (sticker + tax + delivery + install) as the price if those costs are being financed too. If you're paying them up front, leave them out.",
        },
        {
          question: "Why is total interest so much on long terms?",
          answer:
            "Each month you owe interest on the remaining balance. A 60-month term carries that balance five times longer than 12 months, so total interest grows non-linearly with term length — even at the same APR.",
        },
        {
          question: "Should I finance a fridge?",
          answer:
            "Only if the math works for your budget and you have a real plan to pay it off — ideally inside any 0% promo window. If you're paying high APR for years, the energy savings of a new ENERGY STAR unit can be wiped out by interest.",
        },
      ]}
      relatedTools={RELATED}
      cta={{
        title: "See models priced where the math works",
        body: "Browse French door refrigerators sorted by price, capacity, and live retailer pricing.",
        buttonLabel: "Browse French door refrigerators",
        buttonHref: "/collections/french-door-refrigerators",
      }}
    >
      {/* ============ CALCULATOR ============ */}
      <section className="bg-gray-50 py-8 md:py-12">
        <div className="max-w-[1120px] mx-auto px-6 md:px-8 space-y-6">
          <div className="grid lg:grid-cols-2 gap-6 lg:items-stretch">
            {/* Input card */}
            <div className="bg-white rounded-xl p-6 md:p-7 border border-gray-900 flex flex-col">
              <div className="flex items-center gap-3 mb-2">
                <span className="font-mono text-[12px] font-bold tracking-[0.15em] text-[#ff9951]">
                  STEP 01
                </span>
                <div className="flex-1 h-[1px] bg-gray-300" />
              </div>
              <h2
                className="text-gray-900 font-black"
                style={{ fontSize: 28, letterSpacing: "-0.025em", lineHeight: 1.05 }}
              >
                Price.
                <br />
                Term. APR.
              </h2>
              <p className="text-[14px] text-gray-600 mt-2 leading-[1.5]">
                Standard amortization math — same formula every lender uses.
              </p>

              <div className="mt-7 space-y-5">
                <div>
                  <label className="block text-[11px] font-bold uppercase tracking-[0.12em] text-gray-900 mb-2">
                    Refrigerator price
                  </label>
                  <div className="relative">
                    <span className="absolute left-4 top-1/2 -translate-y-1/2 text-[18px] font-bold text-gray-500">$</span>
                    <input
                      type="number"
                      step="50"
                      value={price}
                      onChange={(e) => setPrice(e.target.value)}
                      className="tool-input-clean w-full pl-9 pr-4 py-4 text-[22px] font-bold rounded-lg outline-none border border-gray-300 focus:border-[#ff9951]"
                      style={{ fontVariantNumeric: "tabular-nums" }}
                    />
                  </div>
                  <div className="text-[11px] text-gray-500 mt-1.5 flex items-center gap-1.5">
                    <Info className="w-3.5 h-3.5" /> Include tax + delivery if financed
                  </div>
                </div>

                <div>
                  <label className="block text-[11px] font-bold uppercase tracking-[0.12em] text-gray-900 mb-2">
                    Financing term
                  </label>
                  <select
                    value={term}
                    onChange={(e) => setTerm(e.target.value)}
                    className="tool-input-clean w-full px-4 py-4 text-[18px] font-bold rounded-lg outline-none border border-gray-300 focus:border-[#ff9951] bg-white"
                  >
                    {TERM_OPTIONS.map((t) => (
                      <option key={t} value={t}>
                        {t} months ({t / 12} year{t > 12 ? "s" : ""})
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-[11px] font-bold uppercase tracking-[0.12em] text-gray-900 mb-2">
                    APR
                  </label>
                  <div className="relative">
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      max="35"
                      value={apr}
                      onChange={(e) => setApr(e.target.value)}
                      className="tool-input-clean w-full px-4 py-4 text-[22px] font-bold rounded-lg outline-none border border-gray-300 focus:border-[#ff9951]"
                      style={{ fontVariantNumeric: "tabular-nums" }}
                    />
                    <span className="absolute right-4 top-1/2 -translate-y-1/2 text-[13px] font-mono text-gray-500">
                      %
                    </span>
                  </div>
                  <div className="text-[11px] text-gray-500 mt-1.5 flex items-center gap-1.5">
                    <Info className="w-3.5 h-3.5" /> Enter 0 for promo 0% APR offers
                  </div>
                </div>

                <div className="pt-2 text-[11px] uppercase tracking-[0.12em] text-gray-500 font-bold flex items-center gap-1.5">
                  <RefreshCw className="w-3 h-3" /> Updates live as you type
                </div>
              </div>
            </div>

            {/* Result panel */}
            <div className="flex flex-col">
              <div
                className="rounded-2xl p-6 md:p-8 relative overflow-hidden text-white flex-1"
                style={{ background: "linear-gradient(135deg, #0469f0 0%, #0053c4 100%)" }}
              >
                <div
                  aria-hidden
                  className="absolute top-0 right-0 w-[340px] h-[340px] rounded-full pointer-events-none"
                  style={{
                    background: "rgba(255,153,81,0.35)",
                    filter: "blur(60px)",
                    transform: "translate(25%, -35%)",
                  }}
                />
                <div className="relative">
                  <div className="flex items-center justify-between mb-8 flex-wrap gap-2">
                    <div className="text-[11px] font-bold uppercase tracking-[0.15em] text-white/70">
                      Monthly payment
                    </div>
                    <div className="flex items-center gap-1.5 text-[11px] uppercase tracking-[0.15em] text-white/60">
                      <RefreshCw className="w-3 h-3" /> Updated live
                    </div>
                  </div>
                  <div
                    className="font-black"
                    style={{
                      fontSize: "clamp(36px, 8vw, 88px)",
                      letterSpacing: "-0.045em",
                      lineHeight: 0.9,
                    }}
                  >
                    {`$${result?.monthly.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) ?? "0.00"}`}
                    <span className="text-[20px] md:text-[24px] font-black tracking-tighter text-white/60 ml-2">
                      /mo
                    </span>
                  </div>
                  <div className="mt-4 text-[14px] text-white/80 leading-[1.5]">
                    {result
                      ? result.apr === 0
                        ? `${result.months} equal payments, $0 interest at 0% APR.`
                        : `${result.months} payments at ${result.apr}% APR — total interest is ${result.interestPctOfPrice.toFixed(1)}% of price.`
                      : "Enter price, term, and APR to see your payment."}
                  </div>
                  <div className="grid grid-cols-2 gap-2 sm:gap-3 mt-6 md:mt-8">
                    <div className="rounded-lg p-3 sm:p-4 bg-white/10 border border-white/15">
                      <div className="text-[10px] uppercase tracking-[0.15em] text-white/60 font-bold mb-1">
                        Total cost
                      </div>
                      <div className="text-[18px] sm:text-[22px] font-black tracking-tight">
                        {`$${result?.total.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) ?? "—"}`}
                      </div>
                    </div>
                    <div className="rounded-lg p-3 sm:p-4 bg-white/10 border border-white/15">
                      <div className="text-[10px] uppercase tracking-[0.15em] text-white/60 font-bold mb-1">
                        Total interest
                      </div>
                      <div className="text-[18px] sm:text-[22px] font-black tracking-tight">
                        {`$${result?.interest.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) ?? "—"}`}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ============ FIRST CAROUSEL ============ */}
      <section className="bg-white py-4 md:py-6">
        <ToolProductCarousel
          eyebrow="Step 02 · See real prices"
          title="French door refrigerators across price tiers"
          description="Live retailer pricing — sort by capacity, brand, or sale price."
          collectionType="french-door"
          fallbackCollectionType="bottom-freezer"
        />
      </section>

      {/* ============ METHODOLOGY ============ */}
      <section className="bg-gray-50 py-8 md:py-12 border-y border-gray-200">
        <div className="max-w-[1120px] mx-auto px-6 md:px-8">
          <div className="grid md:grid-cols-[1fr_1.3fr] gap-8 md:gap-12 items-start">
            <div>
              <div className="text-[11px] font-bold uppercase tracking-[0.15em] mb-3 text-[#ff9951]">
                Under the hood
              </div>
              <h2
                className="text-gray-900 font-black"
                style={{ fontSize: "clamp(24px, 3vw, 36px)", letterSpacing: "-0.03em", lineHeight: 1.05 }}
              >
                The same formula <em className="italic text-[#ff9951]">every</em> lender uses.
              </h2>
              <p className="text-[14px] text-gray-600 mt-4 max-w-[400px] leading-[1.55]">
                Standard amortization is the math behind every fixed-payment loan — banks, retailers,
                and credit cards alike. We just expose it directly.
              </p>
            </div>
            <ol className="list-none p-0 m-0 border-t border-gray-900">
              {[
                {
                  n: "01",
                  t: "Convert APR to a monthly rate",
                  d: "Monthly rate r = APR ÷ 100 ÷ 12. A 9.99% APR becomes 0.008325 per month.",
                },
                {
                  n: "02",
                  t: "Apply the amortization formula",
                  d: "Monthly payment = P × r(1+r)ⁿ / ((1+r)ⁿ − 1), where P = principal and n = months.",
                },
                {
                  n: "03",
                  t: "Total interest = total cost − principal",
                  d: "Multiply monthly payment by months to get total cost; subtract the original price to isolate the interest you'll actually pay.",
                },
              ].map((s) => (
                <li
                  key={s.n}
                  className="grid grid-cols-[auto_1fr_auto] gap-4 sm:gap-7 items-center py-5 sm:py-6 border-b border-gray-900"
                >
                  <div className="font-mono text-sm font-bold tracking-[0.05em] min-w-[28px] sm:min-w-[34px] text-[#ff9951]">
                    {s.n}
                  </div>
                  <div>
                    <h3
                      className="text-gray-900"
                      style={{ fontSize: 22, fontWeight: 800, letterSpacing: "-0.02em" }}
                    >
                      {s.t}
                    </h3>
                    <p className="mt-1.5 text-[14px] text-gray-600 leading-[1.55]">{s.d}</p>
                  </div>
                  <ArrowUpRight className="w-4 h-4 sm:w-5 sm:h-5 text-gray-500 flex-shrink-0" />
                </li>
              ))}
            </ol>
          </div>
        </div>
      </section>

      {/* ============ SECOND CAROUSEL ============ */}
      <section className="bg-white py-4 md:py-6">
        <ToolProductCarousel
          eyebrow="You might also like"
          title="Side-by-side refrigerators"
          description="Slimmer footprint, eye-level freezer access, broad price range."
          collectionType="side-by-side"
          fallbackCollectionType="top-freezer"
        />
      </section>
    </ToolPageShell>
  )
}
